import { Typography } from '@neo/primitives/typography'
import { ReactComponent as IcMcp } from '@shared/assets/icons/ic-mcp.svg'
import { Upload, X } from 'lucide-react'
import { useRef, useState } from 'react'
import { ACCEPTED_ICON_TYPES, encodeIcon, MAX_SOURCE_BYTES } from './mcp-icon-encode'

interface McpIconFieldProps {
  /** A PNG, WebP or JPEG data URI, or null for the default MCP mark. */
  value: string | null
  onChange: (value: string | null) => void
}

// The field's default, not a value: `null` still submits no icon.
const DefaultIconMark = () => (
  <span className="flex h-full w-full items-center justify-center rounded bg-bg2 p-3">
    <IcMcp className="h-full w-full text-secondary" />
  </span>
)

export const McpIconField = ({ value, onChange }: McpIconFieldProps) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [error, setError] = useState<string | null>(null)
  const [isEncoding, setIsEncoding] = useState(false)

  const handleFile = async (file: File | undefined) => {
    if (!file) return

    // `accept` is only a picker hint: a drag-drop or a renamed file walks straight past it.
    if (!ACCEPTED_ICON_TYPES.includes(file.type)) {
      setError('Pick a PNG, JPG, WebP, GIF or SVG image.')
      return
    }
    if (file.size > MAX_SOURCE_BYTES) {
      setError(`That file is ${Math.ceil(file.size / (1024 * 1024))} MB. The limit is 5 MB.`)
      return
    }

    setIsEncoding(true)
    try {
      onChange(await encodeIcon(file))
      setError(null)
    } catch (e) {
      setError(
        e instanceof Error && e.message === 'too-large'
          ? 'That image is too detailed to fit. Try a simpler logo.'
          : 'Could not read that image. Try another one.',
      )
    } finally {
      setIsEncoding(false)
    }
  }

  return (
    <div className="flex items-start gap-3">
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={isEncoding}
        aria-busy={isEncoding}
        className="group relative flex h-14 w-14 flex-none items-center justify-center overflow-hidden rounded-lg border border-st1 transition-colors hover:border-secondary"
        aria-label={value ? 'Replace icon' : 'Choose an icon'}
        data-testid="button-mcp-icon"
      >
        {value ? (
          <img src={value} alt="" className="h-full w-full rounded object-contain p-1.5" />
        ) : (
          <DefaultIconMark />
        )}
        {/* A filled tile reads as a preview, so hover says it is the picker. 90% so the icon
            underneath does not tangle with the arrow. */}
        <span
          className="absolute inset-0 flex items-center justify-center rounded-lg bg-foreground/90 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
          aria-hidden="true"
          data-testid="overlay-mcp-icon-upload"
        >
          <Upload className="h-5 w-5 text-background" />
        </span>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_ICON_TYPES.join(',')}
        className="hidden"
        aria-label="Upload icon"
        // Clearing the value is what lets the same file be picked again after an error — without
        // it the input reports no change and the picker looks broken.
        onChange={(event) => {
          void handleFile(event.target.files?.[0])
          event.target.value = ''
        }}
        data-testid="input-mcp-icon"
      />
      <div className="min-w-0 flex-1">
        <Typography variant="P/500" className="text-secondary">
          Icon
        </Typography>
        <Typography variant="XS/400" className="mt-0.5 text-tertiary">
          PNG, JPG, WebP, GIF or SVG. Resized to 256 x 256 px for you.
        </Typography>
        {error && (
          <Typography
            variant="XS/400"
            className="mt-1 text-danger-foreground"
            data-testid="text-mcp-icon-error"
          >
            {error}
          </Typography>
        )}
        {value && !error && (
          <button
            type="button"
            onClick={() => onChange(null)}
            className="mt-1 flex items-center gap-1 text-secondary text-xs-400 hover:text-foreground hover:underline"
            data-testid="button-remove-mcp-icon"
          >
            <X className="h-3 w-3" />
            Remove
          </button>
        )}
      </div>
    </div>
  )
}
